import { useState, useCallback, useRef } from 'react';
import {
  LegacyCard,
  ContextualSaveBar,
  FormLayout,
  Frame,
  Layout,
  Page,
  LegacyStack,
  TextField,
  Banner,
  Toast,
  DropZone,
  Thumbnail,
  Select,
} from '@shopify/polaris';
import { TopBarMarkup, NavigationMarkup, contextControlMarkup } from '../../../../components';
import './CompaniesNew.scss';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../../utils/PrivateRoute';
import axios, { AxiosError } from 'axios';
import { Company, CompanyFile } from '../../../../types';

export function CompaniesNew() {
  const { user } = useUser();
  const navigate = useNavigate();

  const skipToContentRef = useRef<HTMLAnchorElement>(null);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(false);
  const [exist, setExist] = useState(false);
  const [buttonSpinning, setButtonSpinning] = useState(false);
  const [company, setCompany] = useState<Company>({
    name: '',
    type: 'company',
    referent: {
      name: '',
      lastname: '',
      email: '',
      phone: '',
    },
    billing: {
      email: '',
      day: 1,
    },
    platform_link: '',
    platform_username: '',
    platform_password: '',
    phone: '',
    logo: undefined,
  });

  const companyTypes = [
    { label: 'Compagnia', value: 'company' },
    { label: 'Broker/Agenzia', value: 'broker' },
    { label: 'Coverholder', value: 'coverholder' },
  ];

  const [missingFields, setMissingFields] = useState({
    name: false,
    referentName: false,
    referentLastname: false,
    billingEmail: false,
  });
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [urlInvalid, setUrlInvalid] = useState(false);

  const toggleActive = useCallback(() => setActive((active) => !active), []);
  const toggleMobileNavigationActive = useCallback(() => setMobileNavigationActive((mobileNavigationActive) => !mobileNavigationActive), []);

  const handleMobileNavigation = () => {
    setMobileNavigationActive((data) => !data);
  };

  const handleDiscard = useCallback(() => {
    setCompany({
      name: '',
      type: 'company',
      referent: {
        name: '',
        lastname: '',
        email: '',
        phone: '',
      },
      billing: {
        email: '',
        day: 1,
      },
      platform_link: '',
      platform_username: '',
      platform_password: '',
      phone: '',
      logo: undefined,
    });
    setIsDirty(false);
  }, []);

  /** Company drop zone logo */
  const handleDropZoneDrop = useCallback((_dropFiles: File[], acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    // Add necessary file properties for CompanyFile type
    const newLogo: CompanyFile = Object.assign(file, {
      key: file.name,
      title: file.name,
    });

    setCompany((prevCompany) => ({
      ...prevCompany,
      logo: newLogo,
    }));
    setIsDirty(true);
  }, []);

  const uploadedFile = company.logo?.key && (
    <LegacyStack alignment="center">
      <Thumbnail size="large" alt={company.logo.title} source={process.env.REACT_APP_BLOB_IMAGES_URL + company.logo.key} />
      <div>{company.logo.title}</div>
    </LegacyStack>
  );

  const fileUpload = !company.logo?.key ? <DropZone.FileUpload /> : null;

  const logoDropZoneMarkup = (
    <DropZone onDrop={handleDropZoneDrop} allowMultiple={false} label="Logo">
      {uploadedFile}
      {fileUpload}
    </DropZone>
  );

  /** Handle input changes */
  const handleChange = (field: keyof Company | 'referent' | 'billing', value: any, subField?: string) => {
    // Check the missing fields
    if (field === 'name') {
      setMissingFields((prevMissingFields) => ({ ...prevMissingFields, name: !value }));
    } else if (field === 'referent' && subField === 'name') {
      setMissingFields((prevMissingFields) => ({ ...prevMissingFields, referentName: !value }));
    } else if (field === 'referent' && subField === 'lastname') {
      setMissingFields((prevMissingFields) => ({ ...prevMissingFields, referentLastname: !value }));
    } else if (field === 'billing' && subField === 'email') {
      setMissingFields((prevMissingFields) => ({ ...prevMissingFields, billingEmail: !value }));
      setEmailInvalid(false);
    }

    setCompany((prevCompany) => {
      if (field === 'referent' && subField) {
        return { ...prevCompany, referent: { ...prevCompany.referent, [subField]: value } };
      } else if (field === 'billing' && subField) {
        return { ...prevCompany, billing: { ...prevCompany.billing, [subField]: value } };
      } else {
        return { ...prevCompany, [field]: value };
      }
    });
    setIsDirty(true);
  };

  /**
   * Save data
   */
  const handleSave = useCallback(async () => {
    // Check if all required fields are filled
    if (!company.name || !company.referent?.name || !company.referent?.lastname || !company.billing?.email) {
      setMissingFields({
        name: !company.name,
        referentName: !company.referent?.name,
        referentLastname: !company.referent?.lastname,
        billingEmail: !company.billing?.email,
      });
      return;
    }

    // Check if billing email is valid
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(company.billing.email)) {
      setEmailInvalid(true);
      return;
    }

    // Check if platform link is valid
    if (company.platform_link) {
      const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
      if (!urlRegex.test(company.platform_link)) {
        setUrlInvalid(true);
        return;
      }
    }

    try {
      setButtonSpinning(true);
      setError(false);
      setExist(false);

      // Create form data
      const formData = new FormData();
      if (company.logo) {
        formData.append('document', company.logo);
      }
      if (company.name) {
        formData.append('name', company.name);
      }
      if (company.type) {
        formData.append('type', company.type);
      }
      if (company.referent) {
        formData.append('referent[name]', company.referent.name ?? '');
        formData.append('referent[lastname]', company.referent.lastname ?? '');
        formData.append('referent[email]', company.referent.email ?? '');
        formData.append('referent[phone]', company.referent.phone ?? '');
      }
      if (company.billing) {
        formData.append('billing[email]', company.billing.email ?? '');
        formData.append('billing[day]', company.billing.day?.toString() ?? '');
      }
      if (company.platform_link) {
        formData.append('platform_link', company.platform_link);
      }
      if (company.platform_username) {
        formData.append('platform_username', company.platform_username);
      }
      if (company.platform_password) {
        formData.append('platform_password', company.platform_password);
      }
      if (company.phone) {
        formData.append('phone', company.phone);
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL ?? '/api'}/admin/companies/new`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
        },
      });
      const data = response.data;

      if (data.status === 'success') {
        setActive(true);
        setTimeout(() => {
          navigate(`/admin/companies/${data.data._id}`);
        }, 2000);
      } else if (data.status === 'company_exists') {
        setExist(true);
      } else {
        setError(true);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      console.log(axiosError);
      setError(true);
    } finally {
      setButtonSpinning(false);
    }
    setIsDirty(false);
  }, [company, navigate]);

  const contextualSaveBarMarkup = isDirty ? (
    <ContextualSaveBar
      message="Modifiche non salvate"
      saveAction={{
        onAction: handleSave,
        loading: buttonSpinning,
      }}
      discardAction={{
        onAction: handleDiscard,
        discardConfirmationModal: true,
      }}
      contextControl={contextControlMarkup}
    />
  ) : null;

  /**
   * Markups & error
   */
  const saveError = error && (
    <Layout.Section>
      <Banner title="Si è verificato un errore nel salvataggio dei dati" status="critical" onDismiss={() => setError(false)}>
        <p>Si è pregati di riprovare più tardi.</p>
      </Banner>
    </Layout.Section>
  );

  const existError = exist && (
    <Layout.Section>
      <Banner title="Esiste già una compagnia con questo nome" status="critical" onDismiss={() => setExist(false)}>
        <p>Si è pregati di cambiare il nome se si desidera proseguire.</p>
      </Banner>
    </Layout.Section>
  );

  const toastMarkup = active ? <Toast content="La compagnia è stata creata con successo." onDismiss={toggleActive} /> : null;

  // ---- Page markup ----
  const actualPageMarkup = (
    <Page title="Nuova compagnia" backAction={{ content: 'Compagnie', url: '/admin/companies' }}>
      <Layout>
        {/* Banner */}
        {saveError}
        {existError}
        {/* Panoramica compagnia */}
        <Layout.Section>
          <LegacyCard sectioned title="Panoramica compagnia">
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Nome"
                  value={company.name}
                  onChange={(value) => handleChange('name', value)}
                  autoComplete="off"
                  error={missingFields.name && 'Il nome della compagnia è obbligatorio'}
                />
                <Select label="Tipologia" options={companyTypes} value={company.type} onChange={(value) => handleChange('type', value)} />
              </FormLayout.Group>
              <FormLayout.Group>{logoDropZoneMarkup}</FormLayout.Group>
            </FormLayout>
          </LegacyCard>
        </Layout.Section>

        {/* Contatti e piattaforma */}
        <Layout.Section>
          <LegacyCard sectioned title="Contatti e Piattaforma">
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Link alla Piattaforma"
                  value={company.platform_link}
                  onChange={(value) => handleChange('platform_link', value)}
                  autoComplete="off"
                  error={urlInvalid && 'Inserire un URL valido'}
                />
                <TextField
                  label="Numero di telefono della compagnia"
                  value={company.phone}
                  onChange={(value) => handleChange('phone', value)}
                  autoComplete="off"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  label="Username piattaforma"
                  value={company.platform_username}
                  onChange={(value) => handleChange('platform_username', value)}
                  autoComplete="off"
                />
                <TextField
                  label="Password piattaforma"
                  value={company.platform_password}
                  onChange={(value) => handleChange('platform_password', value)}
                  autoComplete="off"
                />
              </FormLayout.Group>
            </FormLayout>
          </LegacyCard>
        </Layout.Section>

        {/* Dati Referente */}
        <Layout.Section>
          <LegacyCard sectioned title="Dati Referente">
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Nome"
                  value={company.referent?.name}
                  onChange={(value) => handleChange('referent', value, 'name')}
                  autoComplete="off"
                  error={missingFields.referentName && 'Il nome del referente è obbligatorio'}
                />
                <TextField
                  label="Cognome"
                  value={company.referent?.lastname}
                  onChange={(value) => handleChange('referent', value, 'lastname')}
                  autoComplete="off"
                  error={missingFields.referentLastname && 'Il cognome del referente è obbligatorio'}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField label="Email" value={company.referent?.email} onChange={(value) => handleChange('referent', value, 'email')} autoComplete="off" />
                <TextField
                  label="Numero di Telefono"
                  value={company.referent?.phone}
                  onChange={(value) => handleChange('referent', value, 'phone')}
                  autoComplete="off"
                />
              </FormLayout.Group>
            </FormLayout>
          </LegacyCard>
        </Layout.Section>

        {/* Dati Fatturazione */}
        <Layout.Section secondary>
          <LegacyCard sectioned title="Dati Fatturazione">
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Email di Fatturazione"
                  type="email"
                  value={company.billing?.email}
                  onChange={(value) => handleChange('billing', value, 'email')}
                  autoComplete="off"
                  error={missingFields.billingEmail ? "L'email di fatturazione è obbligatoria" : emailInvalid && 'Inserire un indirizzo email valido'}
                />
                <Select
                  label="Giorno del Mese per la Fatturazione"
                  options={Array.from({ length: 31 }, (_, i) => ({
                    label: (i + 1).toString(),
                    value: (i + 1).toString(),
                  }))}
                  value={company.billing?.day?.toString()}
                  onChange={(value) => handleChange('billing', parseInt(value), 'day')}
                />
              </FormLayout.Group>
            </FormLayout>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </Page>
  );

  return (
    <Frame
      topBar={<TopBarMarkup user={user} handleMobileNavigation={handleMobileNavigation} />}
      navigation={<NavigationMarkup user={user} />}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigationActive}
      skipToContentTarget={skipToContentRef}
    >
      {contextualSaveBarMarkup}
      {actualPageMarkup}
      {toastMarkup}
    </Frame>
  );
}
